import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { SITE_URL, SITE_BRAND } from "@/lib/seo-config";
import { TRAVAUX_TYPES } from "@/lib/travaux-types";
import {
  ArrowRight, CheckCircle2, Lock, Camera, ShieldCheck,
  Handshake, BadgeCheck, Gift, Receipt, Sparkles,
} from "lucide-react";

function buildTravauxSEO(type) {
  return {
    title: type.seo.title,
    description: type.seo.description,
    keywords: type.seo.keywords,
    canonical: `/travaux/${type.slug}`,
    ogType: "website",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": type.label,
        "serviceType": type.label,
        "description": type.seo.description,
        "provider": {
          "@type": "Organization",
          "name": SITE_BRAND,
          "url": SITE_URL,
        },
        "areaServed": { "@type": "Country", "name": "France" },
        "audience": {
          "@type": "PeopleAudience",
          "audienceType": "Particuliers, promoteurs, syndics, architectes",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": type.faqs.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Travaux", "item": `${SITE_URL}/travaux` },
          { "@type": "ListItem", "position": 3, "name": type.label, "item": `${SITE_URL}/travaux/${type.slug}` },
        ],
      },
    ],
  };
}

const Pillar = ({ icon: Icon, title, body }) => (
  <div className="border-2 border-[#0A0A0B] bg-white p-6 shadow-[3px_3px_0_#0A0A0B]">
    <div className="w-12 h-12 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
    </div>
    <div className="display text-lg leading-tight">{title}</div>
    <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{body}</p>
  </div>
);

export default function TravauxType() {
  const { type: slug } = useParams();
  const type = TRAVAUX_TYPES[slug];

  if (!type) {
    return <Navigate to="/travaux" replace />;
  }

  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid={`travaux-page-${slug}`}>
      <SEO cfg={buildTravauxSEO(type)} />
      <Navbar />

      {/* Hero */}
      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20 relative">
          {/* Breadcrumbs */}
          <nav className="mono text-[11px] uppercase tracking-widest text-zinc-400 mb-6" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-[#FF4D00]">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/travaux" className="hover:text-[#FF4D00]">Travaux</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF4D00]">{type.label}</span>
          </nav>

          <div className="grid lg:grid-cols-[1fr_360px] gap-10 items-end">
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">
                // {type.hero.overline}
              </div>
              <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-3 leading-[0.95]">
                {type.hero.h1}
              </h1>
              <p className="mt-6 text-zinc-300 text-base lg:text-lg max-w-2xl">
                {type.hero.lead}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={`/register?role=demandeur&travaux=${type.slug}`} data-testid={`travaux-cta-mission-${slug}`}>
                  <button className="btn-batisur flex items-center gap-2">
                    Publier ma mission gratuitement <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <Link to="/estimer" data-testid={`travaux-cta-estimer-${slug}`}>
                  <button className="btn-anthracite border-2 border-white/40 flex items-center gap-2">
                    Estimer mon budget
                  </button>
                </Link>
              </div>
            </div>

            {/* Price badge */}
            <div className="border-2 border-[#FF4D00] bg-[#FF4D00]/10 backdrop-blur-sm p-6">
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] font-semibold">Fourchette prix 2026</div>
              <div className="display text-4xl mt-2 text-white leading-tight">{type.hero.priceRange}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mt-2">{type.hero.priceNote}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars — Why Batisur for this work */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20 border-b-2 border-[#0A0A0B]">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Pourquoi Batisur pour ce chantier</div>
        <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
          Zéro arnaque. Zéro chantier abandonné. <span className="text-[#FF4D00]">Zéro stress.</span>
        </h2>
        <p className="mt-4 text-zinc-700 max-w-2xl">
          Les travaux de type "{type.label.toLowerCase()}" sont parmi les plus exposés aux arnaques (acomptes détournés,
          malfaçons cachées, artisan qui disparaît). Batisur ferme ces trois brèches.
        </p>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Pillar
            icon={Lock}
            title="Paiement bloqué chez Stripe"
            body="Aucun acompte versé directement à l'artisan. Vos fonds sont gardés par Stripe (agréé bancaire européen) et libérés uniquement après votre validation."
          />
          <Pillar
            icon={Camera}
            title="Preuves photos horodatées"
            body="Chaque étape du chantier est prouvée par photo GPS + timestamp SHA-256 non falsifiables. Recevables en justice et devant assurance décennale."
          />
          <Pillar
            icon={BadgeCheck}
            title="SIRET vérifié Sirene/INSEE"
            body="L'identité légale de l'artisan est contrôlée en temps réel via la base officielle. Fini les faux professionnels et les entreprises radiées."
          />
          <Pillar
            icon={Handshake}
            title="Médiateurs nommés"
            body="En cas de désaccord, notre équipe médiation Batisur analyse votre dossier avec preuves opposables et tranche sous 30 jours."
          />
          <Pillar
            icon={Receipt}
            title="Facture Factur-X 2026 conforme"
            body="La facture émise est au format PDF/A-3 + XML CII conforme à la norme obligatoire 2026. Comptabilité clean, TVA 20% claire."
          />
          <Pillar
            icon={ShieldCheck}
            title="Garantie décennale vérifiée"
            body="L'assurance décennale de l'artisan est vérifiée à chaque inscription. Vos travaux sont couverts 10 ans pour tous dommages structurels."
          />
        </div>
      </section>

      {/* Sub-métiers with H3 */}
      <section className="bg-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Corps de métier intervenants</div>
          <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
            Quels artisans <span className="text-[#FF4D00]">pour ce chantier ?</span>
          </h2>
          <p className="mt-4 text-zinc-700 max-w-2xl">
            Batisur regroupe tous les corps de métier nécessaires. Vous pouvez publier une mission
            multi-artisans ou une mission par corps de métier — au choix.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {type.submetiers.map((m, i) => (
              <div key={i} className="border-l-4 border-[#FF4D00] pl-5 py-1" data-testid={`travaux-metier-${slug}-${i}`}>
                <h3 className="display text-xl leading-tight">{m.h3}</h3>
                <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Price grid */}
      <section className="bg-[#F7F7F9] border-b-2 border-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Fourchettes prix 2026</div>
          <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 leading-[0.95]">
            Combien ça coûte, <span className="text-[#FF4D00]">précisément ?</span>
          </h2>
          <p className="mt-4 text-zinc-700 max-w-2xl">
            Fourchettes basées sur les tarifs moyens 2026 des artisans SIRET vérifiés Batisur en France.
            Prix TTC pose + fournitures inclus. Devis exact en 48h après publication de votre mission.
          </p>

          <div className="mt-10 border-2 border-[#0A0A0B] bg-white shadow-[6px_6px_0_#FF4D00] overflow-hidden">
            {type.priceGrid.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:gap-6 px-5 py-4 ${
                  i < type.priceGrid.length - 1 ? "border-b border-zinc-200" : ""
                } ${i % 2 === 0 ? "bg-white" : "bg-[#F7F7F9]"}`}
                data-testid={`travaux-price-${slug}-${i}`}
              >
                <div className="text-sm text-zinc-800">{row.label}</div>
                <div className="mono font-semibold text-[#FF4D00] whitespace-nowrap">{row.price}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 border-2 border-[#FF4D00] bg-orange-50 p-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-[#FF4D00]" />
              <div>
                <div className="display text-lg leading-tight">Obtenez un devis précis en 48h</div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 mt-1">Aucun frais · Aucun engagement</div>
              </div>
            </div>
            <Link to={`/register?role=demandeur&travaux=${type.slug}`}>
              <button className="btn-batisur" data-testid={`travaux-cta-pricegrid-${slug}`}>
                Publier ma mission →
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-4xl mx-auto px-6 py-16 lg:py-20">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Questions fréquentes</div>
          <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 leading-[0.95]">
            Vos questions, <span className="text-[#FF4D00]">nos réponses.</span>
          </h2>

          <div className="mt-10 space-y-4">
            {type.faqs.map((f, i) => (
              <details
                key={i}
                className="group border-2 border-[#0A0A0B] bg-white shadow-[3px_3px_0_#0A0A0B]"
                data-testid={`travaux-faq-${slug}-${i}`}
              >
                <summary className="cursor-pointer px-5 py-4 flex items-center justify-between gap-4 list-none">
                  <h3 className="display text-lg leading-tight">{f.q}</h3>
                  <span className="mono text-2xl text-[#FF4D00] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-5 pb-5 pt-2 text-sm text-zinc-700 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#FF4D00] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-white/90 font-semibold">// Prêt à démarrer</div>
            <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-2 leading-tight">
              {type.label} · Publier ma mission maintenant
            </h2>
            <p className="mt-2 text-white/90 max-w-2xl">
              Inscription en 60 secondes. Aucune CB requise. Recevez plusieurs devis en 48h. Vous payez
              uniquement quand vous êtes satisfait — sinon, vous récupérez vos fonds intégralement.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={`/register?role=demandeur&travaux=${type.slug}`}>
              <button className="btn-anthracite shadow-[4px_4px_0_#0A0A0B] flex items-center gap-2" data-testid={`travaux-cta-final-${slug}`}>
                Créer ma mission <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/artisans">
              <button className="border-2 border-white text-white px-6 py-3 font-semibold hover:bg-white hover:text-[#FF4D00] transition">
                Parcourir les artisans
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
